import {
  Button,
  Card,
  ControlledTextField,
  Divider,
  FlatActionList,
  FlatActionListItem,
  Page,
  Stack,
  Switch,
  TextButton,
  View,
} from "@towersystems/roam-common-native";
import { useMutationUpdateGlobalSettings } from "../../../utilities/global-settings/hooks";
import { BackgroundEraserFormValues } from "../../../components/BackgroundEraserForm";
import { useFormContext } from "react-hook-form";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { BackgroundEraserRootParamList } from "../../../types";
import { BgRemovalStrategy } from "@towersystems/roam-common/lib/generated-types";
import { Colors } from "react-native-ui-lib";

export type BackgroundEraserSettingsScreenProps = NativeStackScreenProps<
  BackgroundEraserRootParamList,
  "BackgroundEraserSettingsScreen"
>;

const StrategyMapping: Record<BgRemovalStrategy, string> = {
  [BgRemovalStrategy.RemovalBgStrategy]: "Remove.bg",
  [BgRemovalStrategy.RemovalAiStrategy]: "Removal AI",
};

export const BackgroundEraserSettingsScreen = ({
  navigation,
}: BackgroundEraserSettingsScreenProps) => {
  const { updateGlobalSettings } = useMutationUpdateGlobalSettings();

  const { control, formState, setValue, reset, watch, handleSubmit } =
    useFormContext<BackgroundEraserFormValues>();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const watchedBgRemovalStrategy = watch("bgRemovalStrategy");
  const automaticBackgroundRemoval = watch("automaticBackgroundRemoval");

  const handleOnSubmit = async (input: BackgroundEraserFormValues) => {
    try {
      const result = await updateGlobalSettings({
        automaticBackgroundRemoval: input.automaticBackgroundRemoval,
        bgRemovalStrategy: input.bgRemovalStrategy as BgRemovalStrategy,
        removalBgDetails: {
          apiKey: input.bgApiKey,
        },
        removalAiDetails: {
          apiKey: input.aiApiKey,
        },
      });
      navigation.goBack();
    } catch {
      // settings update failed; navigation stays on this screen
    }
  };

  const actionItems: FlatActionListItem[] = [
    {
      key: "removal_strategy",
      title: "Removal Strategy",
      subTitle: "Choose the background removal strategy.",
      onPress: () => {
        // Navigate to the strategy selection screen
        navigation.navigate("SelectBgRemovalStrategyScreen");
      },
      componentRight: () => {
        return (
          <View>
            <TextButton
              onPress={() => {
                navigation.navigate("SelectBgRemovalStrategyScreen");
              }}
            >
              {watchedBgRemovalStrategy
                ? StrategyMapping[watchedBgRemovalStrategy as BgRemovalStrategy]
                : "Select a strategy"}
            </TextButton>
          </View>
        );
      },
    },
  ];

  const disabled =
    !isDirty || isLoading || isSubmitting || !isValid || isSubmitSuccessful;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label={"Update Settings"}
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page
      title="Background Eraser Settings"
      secondaryTitle="Configure your background eraser settings here."
      footer={footerMarkup}
    >
      <Card>
        <Stack vertical>
          <FlatActionList items={[...actionItems]} />
          <Divider marginVertical={0} />
          {watchedBgRemovalStrategy === BgRemovalStrategy.RemovalBgStrategy && (
            <ControlledTextField
              control={control}
              label="Please enter your background removal API key."
              placeholder="API Key"
              name="bgApiKey"
              filled={false}
            />
          )}
          {watchedBgRemovalStrategy === BgRemovalStrategy.RemovalAiStrategy && (
            <ControlledTextField
              control={control}
              label="Please enter your AI API key."
              placeholder="AI API Key"
              name="aiApiKey"
              filled={false}
            />
          )}
          <Divider marginVertical={0} />
          <FlatActionList
            items={[
              {
                key: "automatic_removal",
                title: "Automatic Background Removal",
                subTitle:
                  "Enable or disable automatic background removal for images.",
                componentRight: () => {
                  return (
                    <View>
                      <Switch
                        value={automaticBackgroundRemoval}
                        onValueChange={() => {
                          setValue(
                            "automaticBackgroundRemoval",
                            !automaticBackgroundRemoval,
                            {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            }
                          );
                        }}
                      />
                    </View>
                  );
                },
              },
            ]}
          />
        </Stack>
      </Card>
    </Page>
  );
};
